import React from "react";
import { Box, Button, Grid, useTheme } from "@mui/material";
import VideocamOutlined from "@mui/icons-material/VideocamOutlined";
import VideoLibraryOutlined from "@mui/icons-material/VideoLibraryOutlined";
import PageHeader from "../../common/PageHeader";
import { GlobalContext } from "../../context/GlobalContext";
import { UserContext } from "../../context/UserContext";
import { useBackgroundJobs } from "../../context/BackgroundJobsContext";
import {
  VIDEO_STUDIO_CATEGORIES,
  getCategoryById,
  POYO_MODELS,
} from "./categories";
import { mediaFieldsForMode, normalizeCategoryId } from "./studioGenerationMode";
import { generateVideo, pollVideoJob } from "./api/generateVideo";
import {
  runAutoStitchedGeneration,
  needsAutoStitch,
  generateSingleSegment,
  joinReadySceneClips,
} from "./api/klingStitchedGeneration";
import { validateFormForModel } from "./modelProfiles";
import {
  formHasScenePlan,
  scenesStructureKey,
  sceneDurationsFromForm,
  removeSceneFromForm,
  normalizeFormForMultiSpeakerDialogue,
} from "./scenePlan";
import CategorySidebar from "./components/CategorySidebar";
import VideoForm, { buildDefaultForm, defaultFormBase } from "./components/VideoForm";
import OutputPanel from "./components/OutputPanel";
import ComingSoonPanel from "./components/ComingSoonPanel";
import ProjectPanel from "./project/ProjectPanel";
import VideoProjectIntro from "./project/VideoProjectIntro";
import { VideoProjectProvider, useVideoProject } from "./project/VideoProjectContext";
import {
  buildFormFromLibraryItem,
  clearLibraryParamsFromUrl,
  pickModelForLibraryItem,
  readLibraryParamsFromSearch,
} from "./promptLibraryBridge";
import {
  clearExploreIdeasPending,
  hydrateFormFromExploreIdeas,
  readExploreIdeasPending,
} from "../exploreIdeas/exploreIdeasBridge";
import {
  VideoPromptTemplatesProvider,
  useVideoPromptTemplates,
} from "./VideoPromptTemplatesContext";

const STATUS_KEYS = {
  idle: "videoStudioStatusIdle",
  queued: "videoStudioStatusQueued",
  generating: "videoStudioStatusGenerating",
  stitching: "videoStudioStatusStitching",
  voiceover: "videoStudioStatusVoiceover",
  done: "videoStudioStatusDone",
  failed: "videoStudioStatusFailed",
};

const STATUS_FALLBACK = {
  idle: "Not started",
  queued: "Queued",
  generating: "Generating",
  stitching: "Stitching clips",
  voiceover: "Adding voiceover",
  done: "Done",
  failed: "Failed",
};

export default function VideoStudio({ lang }) {
  return (
    <VideoPromptTemplatesProvider>
      <VideoProjectProvider>
        <VideoStudioContent lang={lang} />
      </VideoProjectProvider>
    </VideoPromptTemplatesProvider>
  );
}

function VideoStudioContent({ lang }) {
  const theme = useTheme();
  const { showSnack, hitAxios } = React.useContext(GlobalContext);
  const { userData, getUserData } = React.useContext(UserContext);
  const { registerVideoJob, subscribe, jobs: backgroundJobs } = useBackgroundJobs();
  const {
    isInProject,
    clips,
    upsertSegmentClip,
    syncScenePlaceholders,
    removeClip,
    resetSegmentsFrom,
  } = useVideoProject();
  const [pickVideosOpen, setPickVideosOpen] = React.useState(false);
  const { getDefaultPromptForModel, loading: templatesLoading } = useVideoPromptTemplates();

  const [categoryId, setCategoryId] = React.useState("text-to-video");
  const category = getCategoryById(categoryId);

  const [form, setForm] = React.useState(() => defaultFormBase(category));
  const [submitting, setSubmitting] = React.useState(false);
  const [jobId, setJobId] = React.useState(null);
  const [taskId, setTaskId] = React.useState(null);
  const [videoUrl, setVideoUrl] = React.useState(null);
  const [status, setStatus] = React.useState("idle");
  const [segmentProgress, setSegmentProgress] = React.useState(null);
  const [bgVideoJobKey, setBgVideoJobKey] = React.useState(null);
  const [generatingScenes, setGeneratingScenes] = React.useState(() => new Set());
  const generationInFlightRef = React.useRef(false);
  const segmentLocksRef = React.useRef(new Set());
  const autoJoinInFlightRef = React.useRef(false);
  const clipsRef = React.useRef(clips);
  const formRef = React.useRef(form);
  const segmentFailNotifiedRef = React.useRef(false);
  const hydratingJobRef = React.useRef(false);
  const pendingLibraryRef = React.useRef(readLibraryParamsFromSearch());
  const pendingExploreRef = React.useRef(readExploreIdeasPending());
  const libraryAppliedRef = React.useRef(false);
  const exploreAppliedRef = React.useRef(false);
  const skipNextCategoryResetRef = React.useRef(false);

  React.useEffect(() => {
    clipsRef.current = clips;
  }, [clips]);
  React.useEffect(() => {
    formRef.current = form;
  }, [form]);

  const sceneMeta = React.useCallback(
    () => ({
      modelId: form.model,
      modelLabel: POYO_MODELS[form.model]?.label || form.model || "Video",
      aspectRatio: form.aspectRatio || null,
      outputResolution: form.resolution || null,
    }),
    [form.model, form.aspectRatio, form.resolution],
  );

  const sceneStatuses = React.useMemo(() => {
    const map = {};
    for (const c of clips) {
      if (Number(c.segmentIndex) >= 1) map[Number(c.segmentIndex)] = c.status;
    }
    return map;
  }, [clips]);

  // Keep Clip board placeholders aligned with the scene plan.
  // When duration shrinks (or collapses to one clip), prune leftover scene tiles.
  React.useEffect(() => {
    if (!formHasScenePlan(form) || !Array.isArray(form.scenes) || !form.scenes.length) {
      syncScenePlaceholders([], sceneMeta());
      return;
    }
    syncScenePlaceholders(form.scenes, sceneMeta());
  }, [
    form.model,
    form.duration,
    form.hailuoResolution,
    form.resolution,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- structure key tracks count/duration/ids
    scenesStructureKey(form.scenes),
    syncScenePlaceholders,
    sceneMeta,
  ]);

  const notify = React.useCallback(
    (message, success = false) => {
      if (!message) return;
      showSnack?.(message, success);
    },
    [showSnack],
  );

  // Open job from ?job= id (share link or library).
  React.useEffect(() => {
    const raw = new URLSearchParams(window.location.search).get("job");
    const id = Number(raw);
    if (!Number.isFinite(id) || id < 1) return;

    let cancelled = false;
    hydratingJobRef.current = true;
    (async () => {
      const res = await hitAxios({
        path: `/api/video-studio/jobs/${id}`,
        post: false,
        showLoading: false,
        showSnackbar: false,
      });
      if (cancelled || !res?.data?.success) {
        hydratingJobRef.current = false;
        return;
      }
      const job = res.data.data?.job;
      if (!job) {
        hydratingJobRef.current = false;
        return;
      }
      if (job.category_id) setCategoryId(normalizeCategoryId(job.category_id));
      setJobId(job.id);
      setTaskId(job.task_id || null);
      if (
        (job.status === "completed" || job.status === "segment_ready") &&
        job.video_url
      ) {
        setVideoUrl(job.video_url);
        setStatus("done");
      } else if (job.status === "completed" || job.status === "segment_ready") {
        setStatus("done");
      } else if (job.status === "failed") {
        setStatus("failed");
      } else if (job.status === "generating") {
        setStatus("generating");
      } else if (job.status === "pending") {
        setStatus("queued");
      }
      hydratingJobRef.current = false;
    })();

    return () => {
      cancelled = true;
      hydratingJobRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once from URL
  }, []);

  React.useEffect(() => {
    if (bgVideoJobKey) return;
    const videoJobs = backgroundJobs.filter((j) => j.type === "video-studio");
    const latest = videoJobs[videoJobs.length - 1];
    if (!latest) return;
    setBgVideoJobKey(latest.key);
    setJobId(Number(latest.externalId));
    if (latest.phase === "complete" && latest.videoUrl) {
      setVideoUrl(latest.videoUrl);
      setStatus("done");
    } else if (latest.phase === "complete") {
      setStatus("done");
    } else if (latest.phase === "failed") {
      setStatus("failed");
    } else if (latest.phase === "generating" || latest.phase === "queued") {
      setStatus(latest.phase === "queued" ? "queued" : "generating");
    }
  }, [backgroundJobs, bgVideoJobKey]);

  React.useEffect(() => {
    if (!bgVideoJobKey) return undefined;
    return subscribe(bgVideoJobKey, (job) => {
      if (job.type !== "video-studio") return;
      setJobId(Number(job.externalId));
      if (job.phase === "complete" && job.videoUrl) {
        setVideoUrl(job.videoUrl);
        setStatus("done");
        setSubmitting(false);
        generationInFlightRef.current = false;
      } else if (job.phase === "complete") {
        setStatus("done");
        setSubmitting(false);
        generationInFlightRef.current = false;
      } else if (job.phase === "failed") {
        setStatus("failed");
        setSubmitting(false);
        generationInFlightRef.current = false;
      } else if (job.phase === "generating") {
        setStatus("generating");
      }
    });
  }, [bgVideoJobKey, subscribe]);

  // Reset form when switching use case or when templates finish loading — not mid-generation.
  // Also applies Prompt Library deep-link params (?use_case=&model=&prompt=) once.
  React.useEffect(() => {
    if (templatesLoading || generationInFlightRef.current || hydratingJobRef.current) return;

    const explore = pendingExploreRef.current;
    if (explore && !exploreAppliedRef.current) {
      exploreAppliedRef.current = true;
      pendingExploreRef.current = null;
      const resolvedCategoryId = normalizeCategoryId(explore.categoryId || "video-to-video");

      if (resolvedCategoryId !== categoryId) {
        skipNextCategoryResetRef.current = true;
        setCategoryId(resolvedCategoryId);
      }

      hydrateFormFromExploreIdeas(explore, resolvedCategoryId)
        .then((hydrated) => {
          setForm(hydrated);
          clearExploreIdeasPending();
          notify(
            lang?.exploreIdeasStudioPrefilled ||
              "Explore Ideas loaded in Video to Video — review your media and prompt, then generate.",
            true,
          );
        })
        .catch((err) => {
          clearExploreIdeasPending();
          notify(err?.message || "Could not load Explore Ideas media.", false);
        });
      return;
    }

    const lib = pendingLibraryRef.current;
    if (lib && !libraryAppliedRef.current) {
      const resolvedCategoryId =
        lib.use_case && getCategoryById(lib.use_case)
          ? normalizeCategoryId(lib.use_case)
          : categoryId;
      const resolvedCat = getCategoryById(resolvedCategoryId);
      const modelId =
        lib.model && (resolvedCat?.modelIds || []).includes(lib.model)
          ? lib.model
          : pickModelForLibraryItem(resolvedCat, {
              model_ids: lib.model ? [lib.model] : [],
              use_case: lib.use_case,
              prompt: lib.prompt,
            });

      libraryAppliedRef.current = true;
      pendingLibraryRef.current = null;

      if (resolvedCategoryId !== categoryId) {
        skipNextCategoryResetRef.current = true;
        setCategoryId(resolvedCategoryId);
      }

      setForm(
        buildFormFromLibraryItem(resolvedCategoryId, {
          title: lib.prompt_title,
          prompt: lib.prompt,
          use_case: lib.use_case,
          model_ids: modelId ? [modelId] : [],
        }),
      );
      clearLibraryParamsFromUrl();
      return;
    }

    if (skipNextCategoryResetRef.current) {
      skipNextCategoryResetRef.current = false;
      return;
    }

    const cat = getCategoryById(categoryId);
    setForm((prev) => ({
      ...buildDefaultForm(cat, getDefaultPromptForModel),
      ...mediaFieldsForMode(cat.id),
      title: prev.title || "",
    }));
    setJobId(null);
    setTaskId(null);
    setVideoUrl(null);
    setStatus("idle");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset on category / template-load, not fn identity
  }, [categoryId, templatesLoading]);

  const statusLabel = React.useMemo(() => {
    if (segmentProgress?.phase === "generating" && segmentProgress.segment) {
      const tpl =
        lang?.videoStudioSegmentProgress ||
        "Generating segment {current} of {total}…";
      return tpl
        .replace("{current}", String(segmentProgress.segment))
        .replace("{total}", String(segmentProgress.total));
    }
    return lang?.[STATUS_KEYS[status]] || STATUS_FALLBACK[status] || status;
  }, [lang, status, segmentProgress]);

  const finishStitchedResult = async (result) => {
    if (!result?.videoUrl) return;
    setJobId(result.jobId);
    setTaskId(null);
    setVideoUrl(result.videoUrl);
    setStatus("done");
    setSegmentProgress(null);
    try {
      await getUserData?.();
    } catch {
      /* balance refresh best-effort */
    }
    // Keep per-scene clips on Clip board; final join shows in Output.
    notify(
      lang?.videoStudioStitchDone ||
        `Video complete — ${result.segmentCount} scenes joined.`,
      true,
    );
  };

  const markSceneBusy = (seg1, busy) => {
    if (busy) segmentLocksRef.current.add(seg1);
    else segmentLocksRef.current.delete(seg1);
    setGeneratingScenes(new Set(segmentLocksRef.current));
  };

  const tryAutoJoin = React.useCallback(async (justReadyClip = null) => {
    if (autoJoinInFlightRef.current) return;
    if (segmentLocksRef.current.size > 0) return;

    const currentForm = formRef.current;
    if (
      !formHasScenePlan(currentForm) ||
      !Array.isArray(currentForm.scenes) ||
      !currentForm.scenes.length
    ) {
      return;
    }

    const durations = sceneDurationsFromForm(currentForm);
    const total = durations.length;
    let currentClips = [...clipsRef.current];
    if (justReadyClip?.segmentIndex) {
      currentClips = [
        ...currentClips.filter(
          (c) => Number(c.segmentIndex) !== Number(justReadyClip.segmentIndex),
        ),
        { ...justReadyClip, status: "ready" },
      ];
      clipsRef.current = currentClips;
    }
    const ordered = [];
    for (let i = 1; i <= total; i += 1) {
      const clip = currentClips.find(
        (c) => Number(c.segmentIndex) === i && c.status === "ready" && c.url,
      );
      if (!clip) return;
      ordered.push(clip);
    }
    if (ordered.length < 2) return;

    autoJoinInFlightRef.current = true;
    setStatus("stitching");
    setSegmentProgress({ phase: "stitching", total });
    notify(lang?.videoStudioAutoJoinStart || "All scenes ready — joining video…", true);
    try {
      const result = await joinReadySceneClips(hitAxios, currentForm, ordered, {
        onProgress: (info) => {
          if (info.phase === "voiceover") {
            setStatus("voiceover");
            setSegmentProgress({ phase: "voiceover", total: info.total });
          } else {
            setStatus("stitching");
            setSegmentProgress({ phase: "stitching", total: info.total });
          }
        },
      });
      await finishStitchedResult(result);
    } catch (err) {
      setStatus("failed");
      setSegmentProgress(null);
      notify(err?.message || "Could not join scenes.", false);
    } finally {
      autoJoinInFlightRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- finishStitchedResult closes over latest notify/lang
  }, [hitAxios, lang, notify]);

  const stitchedCallbacks = {
    creditsBalance: userData?.credits,
    onProgress: (info) => {
      if (info.phase === "stitching") {
        setStatus("stitching");
        setSegmentProgress({ phase: "stitching", total: info.total });
      } else if (info.phase === "voiceover") {
        setStatus("voiceover");
        setSegmentProgress({ phase: "voiceover", total: info.total });
      } else {
        setStatus("generating");
        setSegmentProgress(info);
      }
    },
    onSegmentStart: ({ segment, total }) => {
      markSceneBusy(segment, true);
      upsertSegmentClip({
        url: "",
        duration: sceneDurationsFromForm(formRef.current)[segment - 1] || 5,
        modelId: formRef.current.model,
        modelLabel: POYO_MODELS[formRef.current.model]?.label || formRef.current.model,
        aspectRatio: formRef.current.aspectRatio || null,
        outputResolution: formRef.current.resolution || null,
        segmentIndex: segment,
        segmentTotal: total,
        status: "generating",
      });
    },
    onSegmentReady: ({ clip, segment }) => {
      upsertSegmentClip(clip);
      markSceneBusy(segment, false);
    },
    onSegmentFailed: ({ clip, error, segment }) => {
      upsertSegmentClip(clip);
      markSceneBusy(segment, false);
      setSegmentProgress(null);
      setStatus("failed");
      segmentFailNotifiedRef.current = true;
      notify(
        error
          ? `${error} Tap play on that scene to generate it again.`
          : lang?.videoStudioSegmentFailed ||
              "A scene failed — tap play on that scene to generate it again.",
        false,
      );
    },
    onVoiceLocked: (lock) => {
      notify(
        (lang?.videoStudioVoicePresetLocked ||
          "Long video: stitching will unify speech to “{label}” — choose a voice that matches your character.")
          .replace("{label}", lock.label || lock.id),
        true,
      );
    },
    isSegmentBusy: (seg1) => segmentLocksRef.current.has(seg1),
    getReadyClip: (seg1) => {
      const c = clipsRef.current.find(
        (x) => Number(x.segmentIndex) === seg1 && x.status === "ready" && x.url,
      );
      return c || null;
    },
  };

  const runGenerationPipeline = async () => {
    setStatus("queued");
    setVideoUrl(null);
    setSegmentProgress(null);

    // Multi-speaker @tags stay in ONE clip (no auto scene-split). Snap overlong
    // durations to the model max so we don't stitch just because of dialogue tags.
    const genForm = normalizeFormForMultiSpeakerDialogue(form);
    if (genForm !== form && Number(genForm.duration) !== Number(form.duration)) {
      setForm(genForm);
      notify(
        lang?.videoStudioMultiSpeakerSingleClip ||
          `Multi-character dialogue runs as one clip (${genForm.duration}s) — both speakers share the frame.`,
        true,
      );
    }

    if (
      needsAutoStitch(genForm.model, genForm.duration, {
        hailuoResolution: genForm.hailuoResolution,
        resolution: genForm.resolution,
      }) ||
      (formHasScenePlan(genForm) &&
        Array.isArray(genForm.scenes) &&
        genForm.scenes.length > 1)
    ) {
      setStatus("generating");
      syncScenePlaceholders(genForm.scenes, sceneMeta());
      const result = await runAutoStitchedGeneration(hitAxios, genForm, categoryId, {
        ...stitchedCallbacks,
        clearBoard: () => {
          syncScenePlaceholders(genForm.scenes, sceneMeta(), { forcePending: true });
        },
        startIndex: 0,
        skipReady: false,
        autoJoin: true,
        seedClips: [],
      });
      if (!result?.skippedJoin) {
        await finishStitchedResult(result);
      }
      return;
    }

    const { jobId: id, taskId: upstreamId } = await generateVideo(hitAxios, genForm, categoryId);
    setJobId(id);
    setTaskId(upstreamId);
    setStatus("generating");
    const key = registerVideoJob(id, {
      title: genForm.title || genForm.prompt,
      categoryId,
    });
    setBgVideoJobKey(key);
    notify(
      lang?.backgroundJobVideoStarted ||
        "Video is generating in the background — you can leave this page. We'll notify you when it's ready.",
      true,
    );
  };

  const handleSubmit = async () => {
    if (!category.ready) return;
    const check = validateFormForModel(form, category.id, lang);
    if (!check.valid) return;
    setSubmitting(true);
    generationInFlightRef.current = true;
    segmentFailNotifiedRef.current = false;
    try {
      await runGenerationPipeline();
    } catch (err) {
      setStatus((prev) => (prev === "done" ? prev : "failed"));
      if (!segmentFailNotifiedRef.current) {
        notify(
          err?.message || lang?.videoStudioSubmitFailed || "Could not start generation.",
          false,
        );
      }
    } finally {
      generationInFlightRef.current = false;
      setSubmitting(false);
      // Clear any leftover locks from failed sequential run
      for (const seg of [...segmentLocksRef.current]) {
        markSceneBusy(seg, false);
      }
    }
  };

  /**
   * Generate a single scene (0-based). Can run in parallel with other scenes.
   */
  const generateSceneAt = async (segmentIndex0, { fromSequential = false } = {}) => {
    if (
      !formHasScenePlan(form) ||
      !Array.isArray(form.scenes) ||
      form.scenes.length < 1
    ) {
      notify(
        lang?.videoStudioRegenSegmentsOnly ||
          "Scene generate is for multi-scene videos — add scenes or raise the total duration.",
        false,
      );
      return;
    }

    const segments = sceneDurationsFromForm(form);
    const total = segments.length;
    if (!total) {
      notify(
        lang?.videoStudioScenesRequired ||
          "Scene list is empty — adjust duration or add a scene.",
        false,
      );
      return;
    }
    const i = Math.max(0, Math.min(total - 1, Math.round(Number(segmentIndex0) || 0)));
    const seg1 = i + 1;

    const check = validateFormForModel(form, category.id, lang, { sceneIndex: i });
    if (!check.valid) {
      notify(check.message || "Cannot generate this scene.", false);
      return;
    }

    if (segmentLocksRef.current.has(seg1)) {
      notify(lang?.videoStudioSceneAlreadyGenerating || "That scene is already generating.", false);
      return;
    }

    markSceneBusy(seg1, true);
    if (!fromSequential) setStatus("generating");
    setSegmentProgress({ phase: "generating", segment: seg1, total });
    upsertSegmentClip({
      url: "",
      duration: segments[i],
      modelId: form.model,
      modelLabel: POYO_MODELS[form.model]?.label || form.model,
      aspectRatio: form.aspectRatio || null,
      outputResolution: form.resolution || null,
      segmentIndex: seg1,
      segmentTotal: total,
      status: "generating",
    });

    try {
      const prev = clipsRef.current.find(
        (c) => Number(c.segmentIndex) === seg1 - 1 && c.status === "ready" && c.url,
      );
      const { clip } = await generateSingleSegment(hitAxios, form, categoryId, {
        segmentIndex0: i,
        chainVideoUrl: prev?.url || null,
        creditsBalance: userData?.credits,
      });
      upsertSegmentClip(clip);
      markSceneBusy(seg1, false);
      try {
        await getUserData?.();
      } catch {
        /* ignore */
      }
      notify(
        (lang?.videoStudioSceneDone || "Scene {n} ready.").replace("{n}", String(seg1)),
        true,
      );

      // Clear / retarget Output "Generating segment…" — tryAutoJoin only takes over when all scenes are ready.
      if (segmentLocksRef.current.size > 0) {
        const nextSeg = Math.min(...segmentLocksRef.current);
        setSegmentProgress({ phase: "generating", segment: nextSeg, total });
        setStatus("generating");
      } else {
        setSegmentProgress(null);
        setStatus((prev) =>
          prev === "done" || prev === "stitching" || prev === "voiceover" ? prev : "idle",
        );
      }

      await tryAutoJoin(clip);
    } catch (err) {
      markSceneBusy(seg1, false);
      upsertSegmentClip({
        url: "",
        duration: segments[i],
        modelId: form.model,
        modelLabel: POYO_MODELS[form.model]?.label || form.model,
        aspectRatio: form.aspectRatio || null,
        outputResolution: form.resolution || null,
        segmentIndex: seg1,
        segmentTotal: total,
        status: "failed",
        errorMessage: err?.message || "Scene generation failed.",
      });
      if (segmentLocksRef.current.size > 0) {
        const nextSeg = Math.min(...segmentLocksRef.current);
        setSegmentProgress({ phase: "generating", segment: nextSeg, total });
        setStatus("generating");
      } else {
        setSegmentProgress(null);
        setStatus((prev) => (prev === "done" ? prev : "failed"));
      }
      notify(err?.message || "Could not generate scene.", false);
    }
  };

  const handleGenerateSceneFromEditor = (sceneIndex0) => {
    generateSceneAt(sceneIndex0);
  };

  const handleGenerateSegment = (segmentIndex1) => {
    generateSceneAt(Math.round(Number(segmentIndex1)) - 1);
  };

  /** Remove a Clip board tile; scene slots also drop from the scene plan + duration. */
  const handleRemoveBoardClip = (clip) => {
    const seg1 = Math.round(Number(clip?.segmentIndex));
    if (
      Number.isFinite(seg1) &&
      seg1 >= 1 &&
      formHasScenePlan(form) &&
      Array.isArray(form.scenes) &&
      form.scenes.length > 0
    ) {
      if (form.scenes.length <= 1) {
        notify(
          lang?.videoStudioNeedOneScene || "Keep at least one scene on the Clip board.",
          false,
        );
        return;
      }
      if (clip?.status === "generating") return;

      const next = removeSceneFromForm(form, seg1 - 1);
      setForm(next);
      if (formHasScenePlan(next) && Array.isArray(next.scenes) && next.scenes.length) {
        // Drop the removed slot and invalidate later scenes (continuity).
        resetSegmentsFrom(seg1, next.scenes, sceneMeta());
      } else {
        // No longer a multi-scene plan — clear segment placeholders via sync.
        syncScenePlaceholders([], sceneMeta());
      }
      return;
    }
    if (clip?.id) removeClip(clip.id);
  };

  const handleRegenerate = async () => {
    if (!category.ready || !validateFormForModel(form, category.id, lang).valid) return;
    if (
      formHasScenePlan(form) &&
      Array.isArray(form.scenes) &&
      form.scenes.length > 1
    ) {
      handleSubmit();
      return;
    }
    if (jobId) {
      setSubmitting(true);
      try {
        setStatus("generating");
        const job = await pollVideoJob(hitAxios, jobId);
        if (
          (job.status === "completed" || job.status === "segment_ready") &&
          job.video_url
        ) {
          setVideoUrl(job.video_url);
          setStatus("done");
          return;
        }
        if (job.status === "completed" || job.status === "segment_ready") {
          setStatus("done");
          return;
        }
        if (job.status === "failed") {
          setStatus("failed");
          notify(job.error_message || "Generation failed.", false);
          return;
        }
        notify(
          lang?.backgroundJobVideoStillProcessing ||
            lang?.videoStudioStillProcessing ||
            "Still processing in the background…",
          true,
        );
      } catch (err) {
        notify(err?.message || "Could not refresh job status.", false);
      } finally {
        setSubmitting(false);
      }
      return;
    }
    handleSubmit();
  };

  const handleShare = () => {
    if (!jobId) return;
    const url = `${window.location.origin}/user?page=video-studio&job=${jobId}`;
    navigator.clipboard?.writeText(url).then(() => {
      notify(lang?.videoStudioShareCopied || "Link copied to clipboard.", true);
    });
  };

  const currentOutputJob = React.useMemo(() => {
    if (!videoUrl || status !== "done") return null;
    return {
      jobId,
      videoUrl,
      modelId: form.model,
      duration: Number(form.duration) || 5,
      resolution: form.resolution || null,
      prompt: form.prompt || "",
    };
  }, [videoUrl, status, jobId, form.model, form.duration, form.resolution, form.prompt]);

  return (
    <Box sx={{ pb: 20 }}>
      <PageHeader
        title={lang?.videoStudioTitle || "Video Studio"}
        subtitle={
          lang?.videoStudioSubtitleWithProject ||
          lang?.videoStudioSubtitle ||
          "Generate clips, add them to Clip board, drag to reorder, then join into one video."
        }
        icon={VideocamOutlined}
        gradientIcon
        primaryAction={
          <Button
            variant="outlined"
            startIcon={<VideoLibraryOutlined />}
            onClick={() => {
              window.location.assign("/user?page=video-library");
            }}
            sx={{ fontWeight: 700 }}
          >
            {lang?.videoLibraryNav || "Video Library"}
          </Button>
        }
      />

      <VideoProjectIntro lang={lang} />
      <ProjectPanel
        lang={lang}
        currentOutputJob={currentOutputJob}
        selectOpen={pickVideosOpen}
        onSelectOpen={() => setPickVideosOpen(true)}
        onSelectClose={() => setPickVideosOpen(false)}
        segmentProgress={segmentProgress}
        onGenerateSegment={handleGenerateSegment}
        onRemoveClip={handleRemoveBoardClip}
        regenerateBusy={false}
      />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {VIDEO_STUDIO_CATEGORIES.length > 1 && (
          <CategorySidebar
            categories={VIDEO_STUDIO_CATEGORIES}
            selectedId={categoryId}
            onSelect={setCategoryId}
            lang={lang}
          />
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} lg={7}>
            {category.ready ? (
              <VideoForm
                category={category}
                form={form}
                onChange={setForm}
                onSubmit={handleSubmit}
                submitting={submitting}
                lang={lang}
                hitAxios={hitAxios}
                onGenerateScene={handleGenerateSceneFromEditor}
                generatingScenes={generatingScenes}
                sceneStatuses={sceneStatuses}
                onReset={() => {
                  setForm(buildDefaultForm(category, getDefaultPromptForModel));
                  setStatus("idle");
                  setJobId(null);
                  setTaskId(null);
                  setVideoUrl(null);
                }}
              />
            ) : (
              <ComingSoonPanel category={category} lang={lang} />
            )}
          </Grid>
          <Grid item xs={12} lg={5}>
              <Box sx={{ position: { lg: "sticky" }, top: theme.spacing(2) }}>
                <OutputPanel
                  status={status}
                  statusLabel={statusLabel}
                  segmentProgress={segmentProgress}
                  taskId={taskId}
                  jobId={jobId}
                  jobTitle={form.title || form.prompt}
                  videoUrl={videoUrl}
                  modelId={form.model}
                  onRegenerate={handleRegenerate}
                  onShare={handleShare}
                  onAddToStitch={() => setPickVideosOpen(true)}
                  inStitchQueue={Boolean(videoUrl && isInProject(videoUrl))}
                  lang={lang}
                />
              </Box>
            </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
